import { Add } from '@mui/icons-material';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getProjectIssues } from '../../../../modules/jira';
import { IssueSearchTableHeaders } from '../../../project-dashboard/test-case/testCaseTableHead';
import Modal from '../../../../ui/layout/Modal';
import TableComponent from '../../../../ui/data/table/TableComponent';
import ActionButton from '../../../../ui/primitives/ActionButton';
import SearchBar from '../../../../ui/primitives/SearchBar';
import JiraIssueLinkage from './JiraIssueLinkage';
import LinkedProjectSelector from '../../../../features/jira/LinkedProjectSelector';

const DEBOUNCE_MS = 500;

const buildJQL = (input, projectKey, linkedProjectId) => {
  const trimmed = typeof input === 'string' ? input.trim() : '';
  if (!trimmed) return `project = ${linkedProjectId}`;

  const keyRegex = /^[A-Za-z][A-Za-z0-9]+-[0-9]+$/;
  const digitsOnly = /^\d+$/;

  if (digitsOnly.test(trimmed)) {
    return `project = ${linkedProjectId} AND key = "${projectKey}-${trimmed}"`;
  }

  if (keyRegex.test(trimmed)) {
    return `project = ${linkedProjectId} AND key = "${trimmed}"`;
  }

  return `project = ${linkedProjectId} AND summary ~ "${trimmed}*"`;
};

const transformIssues = (issues, jiraSite, projectId) =>
  issues.map((issue) => ({
    id: issue.id,
    key: issue.key,
    issuestatus:
      issue.fields.status.status_category?.name ||
      issue.fields.status.name,
    issueType: issue.fields.issuetype.name,
    issueTypeIcon: issue.fields.issuetype.icon_url,
    summary: issue.fields.summary,
    reporter: issue.fields.reporter?.display_name || '',
    priority: issue.fields.priority?.name || '',
    priority_url: issue.fields.priority?.icon_url || '',
    labels: issue.fields.labels || [],
    siteUrl: jiraSite,
    projectId: projectId,
  }));

const SearchIssuesModalComponent = ({
  show,
  setShowSearchModal,
  handleAddIssues,
  linkedIssueIds,
  currentProject,
  step,
  issueKey,
  testCaseData,
  setStep,
  submitting,
  setSubmitting,
  linkedJiraProjects,
  handleShowCreateModal,
  setSelectedProjectFromSearch = () => {},

}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [issues, setIssues] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [err, setErr] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [persistedSelection, setPersistedSelection] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  // Keep selectedProject accessible inside the debounce timeout without stale closure
  const selectedProjectRef = useRef(selectedProject);
  useEffect(() => {
    selectedProjectRef.current = selectedProject;
  }, [selectedProject]);

  const debounceRef = useRef(null);
  const hasLinkedProject = linkedJiraProjects?.length > 0;

  const handleSearch = useCallback(
    async (term, project) => {
      if (!project) return;

      setIsLoading(true);
      setErr('');
      try {
        const projectKey = project.linked_project_key;
        const jql = buildJQL(term, projectKey, project.linked_project_id);

        const body = {
          fields: ['summary', 'status', 'assignee', 'reporter', 'issuetype', 'priority', 'labels'],
          jql,
        };

        const response = await getProjectIssues(body, currentProject?.id);

        if (Array.isArray(response?.issues)) {
          setIssues(transformIssues(response.issues, currentProject?.jira_site_url, project.linked_project_id));
        } else {
          setIssues([]);
        }
      } catch (e) {
        setErr('Error fetching issues. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [currentProject]
  );

  useEffect(() => {
    if (!show) return;
    setSearchTerm('');
    setErr('');
    setIssues([]);

    if (!hasLinkedProject) {
      return;
    }
    const first = linkedJiraProjects[0];
    setSelectedProject(first);
    handleSearch('', first);
  }, [show, linkedJiraProjects]);

  useEffect(() => {
    
    if (!show || !selectedProjectRef.current) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      handleSearch(searchTerm, selectedProjectRef.current);
    }, DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [searchTerm]); 

 
  useEffect(() => {
    const inCurrentSearch = issues.filter(i => selectedIds.includes(i.id));
    const stored = persistedSelection.filter(
      p => selectedIds.includes(p.id) && !issues.some(i => i.id === p.id)
    );

    const merged = [...inCurrentSearch, ...stored];
    const unique = Array.from(new Map(merged.map(i => [i.id, i])).values());

    setSelectedRows(unique);
  }, [issues, selectedIds, persistedSelection]);
  useEffect(() => {
    if (!show) {
      setSelectedIds([]);
      setSelectedRows([]);
      setPersistedSelection([]);
    }
  }, [show]);
  const handleSelectChange = (ids) => {
    setSelectedIds(ids);

    const selectedFromSearch = issues.filter(i => ids.includes(i.id));

    setPersistedSelection(prev => {
      const filteredPrev = prev.filter(p => ids.includes(p.id));
      const merged = [...filteredPrev, ...selectedFromSearch];
      const unique = Array.from(new Map(merged.map(x => [x.id, x])).values());
      return unique;
    });
  };

  const isRowDisabled = useCallback(
    (row) => linkedIssueIds.includes(row.id),
    [linkedIssueIds]
  );

  const handleClose = () => {
    if (submitting) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchTerm('');
    setIssues([]);
    setSelectedIds([]);
    setSelectedRows([]);
    setPersistedSelection([]);
    setErr('');
    setSelectedProject(null);
    setShowSearchModal(false);
    setStep(1);
  };

  const handleAdd = () => {
    if (selectedRows.length > 10) {
      setErr('You can only link a maximum of 10 issues at a time.');
      return;
    }
    handleAddIssues(selectedRows);
  };

  const handleProjectChange = (proj) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSelectedProject(proj);
    handleSearch(searchTerm, proj);
  };
  const handleSearchTermChange = (e) => {
    setSearchTerm(e.target.value);
   
  };

  const handleClearSearch = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchTerm('');
    handleSearch('', selectedProjectRef.current);
  };
  const handleCreateNewIssueClick = () => {
    if (typeof setSelectedProjectFromSearch === 'function') {
      setSelectedProjectFromSearch(selectedProject);
    }
    handleShowCreateModal();
  };
  return (
    <Modal
      open={show}
      handleClose={submitting ? undefined : handleClose}
      modalTitle={step === 1 ? 'Search Issues' : 'Create Issue Links'}
      width="lg"
      action={() => (
        <Stack direction="row" spacing={1}>
          <ActionButton
            label="Close"
            intent="secondary"
            disabled={submitting}
            onClick={handleClose}
          />
          {step === 1 && (
            <ActionButton
              label={submitting ? 'Adding...' : 'Add'}
              intent="primary"
              onClick={handleAdd}
              disabled={submitting || selectedIds.length === 0}
              loading={submitting}
            />
          )}
        </Stack>
      )}
    >
      {!hasLinkedProject ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          No external project is linked with this project. Please link a project first.
        </Alert>
      ) : step === 1 ? (
        <Box>
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
            <Box sx={{ minWidth: 200 }}>
              <LinkedProjectSelector
                linkedProjects={linkedJiraProjects}
                onProjectChange={handleProjectChange}
                selectedProjectId={selectedProject?.linked_project_id}
              />
            </Box>

            <Box sx={{ flex: 1, minWidth: 220 }}>
              <SearchBar
                placeholder="Search issue key, ID, or summary"
                value={searchTerm}
                onChange={handleSearchTermChange}
                clearSearch={handleClearSearch}
                width="100%"
              />
            </Box>

            <ActionButton
              label="Create New Issue"
              intent="primary"
              icon={<Add />}
              onClick={handleCreateNewIssueClick}
              disabled={isLoading || submitting}
            />
          </Stack>

          {selectedRows.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Selected Issues: </strong>
                {selectedRows.map((r) => r.key).join(', ')}
              </Typography>
            </Box>
          )}

          {err && (
            <Alert severity="error" sx={{ my: 1.5 }}>
              {err}
            </Alert>
          )}

          <Box sx={{ maxHeight: 300, overflowY: 'auto', mt: 2 }}>
            <TableComponent
              data={issues}
              headerTabs={IssueSearchTableHeaders}
              isLoading={isLoading}
              showCheckbox
              selected={selectedIds}
              setSelected={handleSelectChange}
              isRowDisabled={isRowDisabled}
            />
          </Box>
        </Box>
      ) : (
        <JiraIssueLinkage
          testCaseId={testCaseData?.id}
          issueKey={issueKey}
          projectId={currentProject?.id}
          handleModalClose={handleClose}
          type="search"
          setSubmitting={setSubmitting}
        />
      )}
    </Modal>
  );
};

export default SearchIssuesModalComponent;
